import { useRef } from 'react';
import { useKey, useIsomorphicLayoutEffect } from 'react-use';
import { useUsingKeyboard } from './useUsingKeyboard';

const FOCUSABLE_ELEMENTS = [
  'a[href]',
  'area[href]',
  'input:not([disabled]):not([type="hidden"]):not([aria-hidden])',
  'select:not([disabled]):not([aria-hidden])',
  'textarea:not([disabled]):not([aria-hidden])',
  'button:not([disabled]):not([aria-hidden])',
  'iframe',
  'object',
  'embed',
  '[contenteditable]',
  '[tabindex]:not([tabindex^="-"])',
];

const getFocusableNodes = (element) => {
  const nodes = element.querySelectorAll(FOCUSABLE_ELEMENTS);
  return Array(...nodes).filter((node) => node.offsetParent !== null); //Filters nodes which are hidden to prevent focus leak outside modal
};

export const useFocusTrap = (shouldFocus = false) => {
  const ref = useRef(null);
  const isUsingKeyboard = useUsingKeyboard();

  useKey(
    'Tab',
    (event) => {
      if (!shouldFocus || !ref.current) return;

      const containerEl = ref.current;
      const focusableNodes = getFocusableNodes(containerEl);

      if (focusableNodes.length === 0) return;

      const focusedItemIndex = focusableNodes.indexOf(document.activeElement);

      if (event.shiftKey && focusedItemIndex === 0) {
        focusableNodes[focusableNodes.length - 1].focus();
        event.preventDefault();
      }

      if (!event.shiftKey && focusableNodes.length > 0 && focusedItemIndex === focusableNodes.length - 1) {
        focusableNodes[0].focus();
        event.preventDefault();
      }

      // Return focus to the trap if removed by some other means
      if (focusedItemIndex === -1) {
        focusableNodes[0].focus();
      }
    },
    {},
    [shouldFocus],
  );

  useIsomorphicLayoutEffect(() => {
    if (ref.current && isUsingKeyboard && shouldFocus) {
      // Set focus to first node
      const focusableNodes = getFocusableNodes(ref.current);
      if (focusableNodes.length > 0) focusableNodes[0].focus();
    }
  }, [shouldFocus]);

  return ref;
};
